import { PageController } from '@client/components/_core/layout/PageController';
import { PageLoading } from '@client/components/_ui/feedback/PageLoading';
import { RequireAuth } from '@client/components/auth/RequireAuth';
import { whatsNewHash } from '@client/components/whats-new/whats-new-eligibility';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { selectOrg, useAuthStore } from '@client/stores/AuthStore';
import { SYSTEM_ORG_IDS } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';

/**
 * Organisation-agnostic entry to a What's new release (CUR-1094 req. 4/5):
 * `/whats-new/registers` is the link the launch email and support replies
 * carry, since neither knows the reader's organisation. Signed-out visitors go
 * through login with this path as the return target (a path survives Auth0
 * where a hash would not); signed-in ones are forwarded straight to their
 * console home with `#whats-new/<id>`, where the shell opens the note. Outside
 * the release's audience the hash simply opens nothing.
 */
export const Route = createFileRoute('/whats-new/$releaseId')({
    staticData: {
        ui: { title: "What's new" },
    },
    component: WhatsNewEntry,
});

function WhatsNewEntry() {
    return (
        <PageController route={Route}>
            <RequireAuth>
                <ForwardToConsole />
            </RequireAuth>
        </PageController>
    );
}

function ForwardToConsole() {
    const { releaseId } = Route.useParams();
    const org = useAuthStore(selectOrg);
    const navigate = useNavigate();

    useEffect(() => {
        if (!org) return;
        if (org.id === SYSTEM_ORG_IDS.SYSTEM_CONSOLE) {
            // The system console has no What's new surface; land on its home.
            navigate({ to: '/admin-console', replace: true });
            return;
        }
        navigate({ to: '/console/$orgId', params: { orgId: org.id }, hash: whatsNewHash(releaseId), replace: true });
    }, [org, releaseId, navigate]);

    return <PageLoading />;
}
